import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdReturnsIdBody,
  PostAdminV1StoresStoreIdOrdersIdReturnsBody,
  PostAdminV1StoresStoreIdReturnsIdResolveBody,
  Return,
} from '@nucleus-commerce/api-client/admin';

import { giftCardKeys } from '@/features/gift-cards';
import { orderKeys } from '@/features/orders';

import { returnsApi } from '../api/returns-api';

import { returnKeys } from './return-keys';

/**
 * The order carries its returns and a timeline entry per step, so every change refetches the
 * order as well as the return lists.
 */
function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, ret: Return) {
  queryClient.setQueryData(returnKeys.detail(storeId, ret.id), ret);
  void queryClient.invalidateQueries({ queryKey: orderKeys.detail(storeId, ret.orderId) });
  return queryClient.invalidateQueries({ queryKey: returnKeys.lists(storeId) });
}

/** 422 on `lines[i].quantity` (with `meta.remaining`) above what shipped and is not returned. */
export function useOpenReturn(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      body,
    }: {
      orderId: string;
      body: PostAdminV1StoresStoreIdOrdersIdReturnsBody;
    }) => returnsApi.open(storeId, orderId, body),
    meta: { successMessage: 'returns:toasts.opened', silentError: true },
    onSuccess: (ret) => remember(queryClient, storeId, ret),
  });
}

/** 409 with `meta.allowed` on a move the workflow does not allow; 422 on missing conditions. */
export function useUpdateReturn(storeId: string, successMessage = 'returns:toasts.updated') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdReturnsIdBody }) =>
      returnsApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (ret) => remember(queryClient, storeId, ret),
  });
}

/**
 * The response carries a store-credit code once. It is handed to the dialog and kept out of the
 * cache (the stored detail is the plain return), so it cannot resurface on another screen.
 * Refunds and store credit also touch the order totals and the gift card list.
 */
export function useResolveReturn(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdReturnsIdResolveBody;
    }) => returnsApi.resolve(storeId, id, body),
    meta: { successMessage: 'returns:toasts.resolved', silentError: true },
    onSuccess: ({ storeCredit: _code, ...ret }) => {
      void queryClient.invalidateQueries({ queryKey: giftCardKeys.all(storeId) });
      return remember(queryClient, storeId, ret);
    },
  });
}
